package top.wcpe.allincore.ui.runtime.render

import top.wcpe.allincore.ui.runtime.internal.text.charCountOf
import top.wcpe.allincore.ui.runtime.internal.text.codePointAtCompat
import kotlin.jvm.JvmStatic

/**
 * 文本换行纯函数：先按 `\n` 分段，再按可用宽度逐码点截断。
 *
 * <p>算法源自历史上 `ButtonRenderer.wrapText`（换行规则保持一致）：</p>
 * <ul>
 *   <li>先按 `\n` 切成段落（保留空段落，即连续换行产生空行）；</li>
 *   <li>`availableWidth <= 0` 时跳过逐码点断行，仅按 `\n` 分段——避免首帧
 *       box.width 还未由 Effect 求值（响应式属性绑定首帧值可能为 0）时把文本拉成单字符竖排；</li>
 *   <li>否则对每个段落逐<b>码点</b>累加宽度截断，`end > start` 守卫保证至少前进一个码点。</li>
 * </ul>
 *
 * <p>按码点（非 code unit）推进，使代理对（emoji 等增补平面字符占 2 个 char）永不被换行点劈裂：
 * 还原出的视觉行行首真实偏移始终落在合法 code point 边界，与 `TextLayout` 类文档承诺
 * 「偏移永不落在代理对中间」一致。BMP 普通文本（每码点 1 个 char）行为与逐字符截断完全等价，
 * 仅在代理对恰好压在换行点的罕见情形下把整个码点整体推到下一行（正确行为）。</p>
 *
 * <p>测宽通过 `TextLayout.Measurer` 注入，无平台 / 渲染依赖，可单测。
 * 调用方（ButtonRenderer / TextLayout 多行）各自适配自己的字体 / 缩放测宽函数。</p>
 */
object TextWrapper {

    /**
     * 按可用宽度对文本软换行。
     *
     * @param text           原始文本（`null` 按空串处理）
     * @param availableWidth 可用宽度；`<= 0` 时仅按 `\n` 分段
     * @param measurer       测宽函数
     * @return 视觉行文本列表（含因 `\n` 产生的空行）
     */
    @JvmStatic
    fun wrap(text: String?, availableWidth: Float, measurer: TextLayout.Measurer): List<String> {
        val result = mutableListOf<String>()
        val src = text ?: ""
        val paragraphs = src.split("\n")
        if (availableWidth <= 0f) {
            // box 宽度未就绪：仅按 \n 切分，不做逐字换行
            result.addAll(paragraphs)
            return result
        }
        for (paragraph in paragraphs) {
            if (paragraph.isEmpty()) {
                result.add("")
                continue
            }
            // 按码点宽度截断（逐码点推进，代理对永不被劈裂）
            var start = 0
            while (start < paragraph.length) {
                var accWidth = 0f
                var end = start
                while (end < paragraph.length) {
                    val cp = paragraph.codePointAtCompat(end)
                    val charCount = charCountOf(cp)
                    val cpWidth = measurer.width(paragraph.substring(end, end + charCount))
                    if (accWidth + cpWidth > availableWidth && end > start) {
                        break
                    }
                    accWidth += cpWidth
                    end += charCount
                }
                result.add(paragraph.substring(start, end))
                start = end
            }
        }
        return result
    }
}
